// src/components/ActivePositions.js
import React, { useEffect, useMemo, useState } from 'react';
import { useUser } from '../../contexts/UserContext';
import { formatNumberToIndianLocale } from '../../utils/formatNumber';
import { closePositionRequest } from './api';
import { resolveCloseAction } from './helpers';
import './ActivePositions.css';
import GlassPaper from '../GlassPaper';

const ActivePositions = () => {
    const { user } = useUser();
    const [positions, setPositions] = useState([]);
    const [closeDialog, setCloseDialog] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feedback, setFeedback] = useState(null);

    useEffect(() => {
        if (user) {
            const ws = new WebSocket(process.env.NODE_ENV === 'production'
                ? 'wss://insights.contraadvisory.net/ws'
                : 'ws://192.168.1.114:5000/ws');

            ws.onopen = () => {
                console.log('WebSocket connected');
            };

            ws.onmessage = (event) => {
                const data = JSON.parse(event.data);
                setPositions(data);
            };

            ws.onclose = () => {
                console.log('WebSocket disconnected');
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
            };

            return () => {
                ws.close();
            };
        }
    }, [user]);

    useEffect(() => {
        if (!feedback?.message) {
            return undefined;
        }

        const timeoutId = setTimeout(() => setFeedback(null), 5000);
        return () => clearTimeout(timeoutId);
    }, [feedback]);

    const groupedPositions = useMemo(() => {
        return positions.reduce((acc, position) => {
            const brokerAlias = position.broker_alias || position.app_name || 'Unknown Broker';
            if (!acc[brokerAlias]) {
                acc[brokerAlias] = [];
            }
            acc[brokerAlias].push(position);
            return acc;
        }, {});
    }, [positions]);

    const getCellStyle = (value) => {
        const number = parseFloat(value);
        if (number > 0) {
            return { color: 'green' };
        } else if (number < 0) {
            return { color: 'red' };
        } else {
            return { color: 'grey' };
        }
    };

    const handlePromptClose = (position, brokerAlias) => {
        const action = resolveCloseAction(position.quantity);
        if (!action || !position.account_id || !position.exchange) {
            return;
        }

        setFeedback(null);
        setCloseDialog({
            accountId: position.account_id,
            appName: position.app_name,
            tradingsymbol: position.tradingsymbol,
            exchange: position.exchange,
            product: position.product,
            quantity: Number(position.quantity),
            action,
            brokerAlias,
        });
    };

    const handleConfirmClose = async () => {
        if (!closeDialog) {
            return;
        }

        setIsSubmitting(true);
        try {
            await closePositionRequest({
                accountId: closeDialog.accountId,
                tradingsymbol: closeDialog.tradingsymbol,
                exchange: closeDialog.exchange,
                quantity: closeDialog.quantity,
                product: closeDialog.product,
            });
            setFeedback({
                type: 'success',
                message: `Close order placed for ${closeDialog.tradingsymbol} (${formatNumberToIndianLocale(closeDialog.action.absoluteQuantity)}).`,
            });
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to close position. Please try again.';
            setFeedback({
                type: 'error',
                message,
            });
        } finally {
            setIsSubmitting(false);
            setCloseDialog(null);
        }
    };

    const handleDismissDialog = () => {
        if (!isSubmitting) {
            setCloseDialog(null);
        }
    };

    return (
        <div>
            <h2 className="dashboard-title positions-title">Active Positions</h2>
            {feedback && (
                <div className={`position-feedback position-feedback--${feedback.type}`} role="alert">
                    {feedback.message}
                </div>
            )}
            <div className="positions-grid">
                {Object.entries(groupedPositions).map(([brokerAlias, brokerPositions]) => {
                    const brokerPnl = brokerPositions.reduce((total, position) => total + Number(position.pnl || 0), 0);

                    return (
                        <GlassPaper className="positions-card" elevation={0} square={false} component="section" key={brokerAlias}>
                            <header className="positions-card__header">
                                <h3>{brokerAlias}</h3>
                                <span className="positions-card__pnl" style={getCellStyle(brokerPnl)}>
                                    {formatNumberToIndianLocale(brokerPnl)}
                                </span>
                            </header>
                            <div className="positions-card__body">
                                <div className="positions-card__table-wrapper">
                                    <table className="positions-table">
                                        <thead>
                                            <tr>
                                                <th>Symbol</th>
                                                <th className="text-right">Qty.</th>
                                                <th className="text-right">Avg.</th>
                                                <th className="text-right">LTP</th>
                                                <th className="text-right">PnL</th>
                                                <th className="text-right position-actions-heading">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {brokerPositions.map((position, index) => (
                                                <tr key={index} className="position-row">
                                                    <td>{position.tradingsymbol}</td>
                                                    <td className="text-right" style={getCellStyle(position.quantity)}>
                                                        {formatNumberToIndianLocale(position.quantity)}
                                                    </td>
                                                    <td className="text-right">{formatNumberToIndianLocale(position.average_price)}</td>
                                                    <td className="text-right">{formatNumberToIndianLocale(position.last_price)}</td>
                                                    <td className="text-right" style={getCellStyle(position.pnl)}>
                                                        {formatNumberToIndianLocale(position.pnl)}
                                                    </td>
                                                    <td className="text-right position-actions-cell">
                                                        {resolveCloseAction(position.quantity) && position.account_id && position.exchange ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger position-close-btn"
                                                                onClick={() => handlePromptClose(position, brokerAlias)}
                                                                disabled={isSubmitting}
                                                            >
                                                                Close
                                                            </button>
                                                        ) : (
                                                            <span className="position-actions-placeholder">—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </GlassPaper>
                    );
                })}
            </div>
            {closeDialog && (
                <div className="position-close-dialog-backdrop" role="presentation" onClick={handleDismissDialog}>
                    <GlassPaper
                        className="position-close-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="position-close-dialog-title"
                        component="div"
                        elevation={0}
                        square={false}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <h3 id="position-close-dialog-title" className="position-close-dialog__title">
                            Close Position
                        </h3>
                        <p className="position-close-dialog__message">
                            Do you really want to close the position in <strong>{closeDialog.tradingsymbol}</strong> for{' '}
                            <strong>{closeDialog.brokerAlias}</strong>?
                        </p>
                        <p className="position-close-dialog__details">
                            Action:{' '}
                            <span className="position-close-dialog__highlight">
                                {closeDialog.action.side === 'SELL' ? 'Sell' : 'Buy'} {formatNumberToIndianLocale(closeDialog.action.absoluteQuantity)} @ Market
                            </span>
                        </p>
                        <div className="position-close-dialog__actions">
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={handleDismissDialog}
                                disabled={isSubmitting}
                            >
                                No
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={handleConfirmClose}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Closing…' : 'Yes, Close'}
                            </button>
                        </div>
                    </GlassPaper>
                </div>
            )}
        </div>
    );
};

export default ActivePositions;
